const rows = [
  {
    platform: 'Zoom',
    dialback: 'Yes — "Call Me" and "Auto Call" (v6.5+). Calls you once at meeting start. Requires Premium Audio add-on for full callout. Some institutions have disabled it due to cost.',
    retry: { label: 'No retry', type: 'none' },
    agnostic: { label: 'Zoom only', type: 'none' },
    joins: { label: 'No', type: 'none' },
    jessica: false,
  },
  {
    platform: 'Microsoft Teams',
    dialback: 'Dial-out exists, but requires a human organizer to manually initiate it from inside the meeting. Audio Conferencing add-on required. No automated personal callback.',
    retry: { label: 'No retry', type: 'none' },
    agnostic: { label: 'Teams only', type: 'none' },
    joins: { label: 'No', type: 'none' },
    jessica: false,
  },
  {
    platform: 'Google Meet',
    dialback: 'Meet can call your phone when joining — but only if you are already paying attention and request it from within an active session. No pre-scheduled callback.',
    retry: { label: 'No retry', type: 'none' },
    agnostic: { label: 'Meet only', type: 'none' },
    joins: { label: 'No', type: 'none' },
    jessica: false,
  },
  {
    platform: 'Cisco Webex',
    dialback: 'Robust dial-out infrastructure — organizers can dial out to participants. But it requires a human to initiate from inside the meeting. No automated personal callback system.',
    retry: { label: 'No retry', type: 'none' },
    agnostic: { label: 'Webex only', type: 'none' },
    joins: { label: 'No', type: 'none' },
    jessica: false,
  },
  {
    platform: 'Jessica · Dial-Back',
    dialback: 'Reads your calendar, joins your bridge autonomously, then calls your phone directly. No human required to initiate. Works across any platform you\'re invited to.',
    retry: { label: 'Retries until answered or cancelled', type: 'yes' },
    agnostic: { label: 'Platform-agnostic', type: 'yes' },
    joins: { label: 'Yes — from minute one', type: 'yes' },
    jessica: true,
  },
];

function Tag({ label, type }: { label: string; type: 'none' | 'partial' | 'yes' }) {
  const styles = {
    none: 'bg-white/5 text-[#9a9488]',
    partial: 'bg-[rgba(200,180,42,0.12)] text-[#c8b42a]',
    yes: 'bg-[rgba(200,71,42,0.15)] text-[#c8472a]',
  };
  return (
    <span
      className={`inline-block px-2 py-[2px] rounded-sm text-[10px] tracking-[0.06em] ${styles[type]}`}
      style={{ fontFamily: "'DM Mono', monospace" }}
    >
      {label}
    </span>
  );
}

export default function DialBackComparison() {
  return (
    <section
      className="py-28 px-12 border-t border-white/[0.06] max-md:px-6 max-md:py-20"
      style={{ background: 'rgba(255,255,255,0.02)', fontFamily: "'Instrument Sans', sans-serif" }}
    >
      <div className="max-w-[1100px] mx-auto">
        <div
          className="flex items-center gap-3 mb-4"
          style={{ fontFamily: "'DM Mono', monospace", fontSize: '11px', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#c8472a' }}
        >
          <span className="block w-6 h-px bg-[#c8472a]" />
          Platform comparison
        </div>

        <h2
          className="font-light leading-[1.1] text-[#f5f2ec]"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 'clamp(2.2rem, 4vw, 3.5rem)' }}
        >
          What the major platforms<br />
          <em className="italic text-[#9a9488]">actually</em> offer
        </h2>

        <div className="mt-12 overflow-x-auto">
          <table className="w-full border-collapse text-[0.9rem]">
            <thead>
              <tr>
                {['Platform', 'Dial-back / call-me feature', 'Retries if you don\'t answer', 'Platform-agnostic', 'Joins before you do'].map((h, i) => (
                  <th
                    key={i}
                    className="text-left px-4 py-[10px] border-b border-white/[0.08] text-[#9a9488]"
                    style={{ fontFamily: "'DM Mono', monospace", fontSize: '10px', fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', width: i === 0 ? '18%' : undefined }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={i}
                  className={`transition-colors duration-150 ${
                    row.jessica
                      ? 'bg-[rgba(200,71,42,0.07)]'
                      : 'hover:bg-white/[0.015]'
                  }`}
                >
                  <td
                    className={`px-4 py-4 border-b text-[0.88rem] font-semibold ${
                      row.jessica
                        ? 'text-[#c8472a] border-b-[rgba(200,71,42,0.15)]'
                        : 'text-[#f5f2ec] border-b-white/[0.04]'
                    }`}
                  >
                    {row.platform}
                  </td>
                  <td
                    className={`px-4 py-4 border-b leading-[1.5] text-[rgba(245,242,236,0.7)] ${
                      row.jessica ? 'border-b-[rgba(200,71,42,0.15)] text-[rgba(245,242,236,0.9)]' : 'border-b-white/[0.04]'
                    }`}
                  >
                    {row.dialback}
                  </td>
                  <td className={`px-4 py-4 border-b ${row.jessica ? 'border-b-[rgba(200,71,42,0.15)]' : 'border-b-white/[0.04]'}`}>
                    <Tag label={row.retry.label} type={row.retry.type as 'none' | 'partial' | 'yes'} />
                  </td>
                  <td className={`px-4 py-4 border-b ${row.jessica ? 'border-b-[rgba(200,71,42,0.15)]' : 'border-b-white/[0.04]'}`}>
                    <Tag label={row.agnostic.label} type={row.agnostic.type as 'none' | 'partial' | 'yes'} />
                  </td>
                  <td className={`px-4 py-4 border-b ${row.jessica ? 'border-b-[rgba(200,71,42,0.15)]' : 'border-b-white/[0.04]'}`}>
                    <Tag label={row.joins.label} type={row.joins.type as 'none' | 'partial' | 'yes'} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="mt-6 text-[0.85rem] text-[#9a9488] leading-[1.6]">
          The common thread across every existing platform: they put the burden of awareness back on you. You must already know the meeting is happening to take action. Which is precisely the problem when you&apos;ve time-traveled.
        </p>
      </div>
    </section>
  );
}
